import { useEffect, useState } from 'react';
import { Plus, Loader2, Trash2, Copy, KeyRound, Webhook, AlertCircle, ExternalLink, Eye, BookOpen } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmModal } from '@/components/ConfirmModal';
import { LockedFeatureCard, SectionLoadingCard } from '@/components/LockedFeatureCard';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantPlan } from '@/hooks/useTenantPlan';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';

const ALL_EVENTS = ['lead.created', 'lead.updated', 'lead.merged', 'lead.deleted'] as const;

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

interface WebhookSub {
  id: string;
  url: string;
  events: string[];
  isActive: boolean;
  description: string | null;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: string | null;
  consecutiveFailures: number;
  createdAt: string;
}

export function DeveloperPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || (user as any)?.isOwner;

  // Pull the tenant's plan via the shared hook. It dedupes /subscriptions/me
  // across all gated pages in the session, so navigating between Developer,
  // Audit Log, and the AI assistant doesn't refetch the same payload.
  const { hasFeature, planName, loading: planLoading } = useTenantPlan();

  if (!isAdmin) {
    return (
      <DashboardLayout title="Developer">
        <Card><CardContent className="p-6">
          <p className="text-sm text-slate-500">Only admins can manage API keys and webhooks.</p>
        </CardContent></Card>
      </DashboardLayout>
    );
  }

  const hasApiAccess = hasFeature('public_api');
  const hasWebhooksAccess = hasFeature('webhooks');

  return (
    <DashboardLayout title="Developer">
      <div className="space-y-5">
        {/* Docs banner — sends admins (and the developers they share
            this page with) straight to the public API reference at
            /docs/api. Public route, no auth required, share-friendly. */}
        <a
          href="/docs/api"
          target="_blank"
          rel="noreferrer"
          className="block rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-white p-4 hover:border-indigo-300 transition-colors group"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center flex-shrink-0">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                API Documentation
                <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#6366F1]" />
              </p>
              <p className="text-sm text-slate-600 mt-0.5">
                Endpoints, request/response samples, webhook events, signature
                verification — share this URL with whoever is integrating with BrixLead.
              </p>
              <p className="text-xs text-[#6366F1] font-mono mt-1">/docs/api</p>
            </div>
          </div>
        </a>

        <ApiKeysSection unlocked={hasApiAccess} planName={planName} planLoading={planLoading} />
        <WebhooksSection unlocked={hasWebhooksAccess} planName={planName} planLoading={planLoading} />
      </div>
    </DashboardLayout>
  );
}

// ─── API Keys ─────────────────────────────────────────────────────────────
function ApiKeysSection({ unlocked, planName, planLoading }: { unlocked: boolean; planName: string; planLoading: boolean }) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<{ rawKey: string; name: string } | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<ApiKey[]>('/api-keys');
      setKeys(data);
    } finally { setLoading(false); }
  };

  // Only fire the gated GET if the tenant actually has the feature. Otherwise
  // we'd 402 on every Developer-page mount and the upgrade modal would pop up
  // unprompted — bad UX. Locked tenants see the LockedFeatureCard instead.
  useEffect(() => {
    if (!unlocked) { setLoading(false); return; }
    load();
  }, [unlocked]);

  if (planLoading) return <SectionLoadingCard />;

  if (!unlocked) {
    return (
      <LockedFeatureCard
        icon={<KeyRound className="h-5 w-5 text-[#6366F1]" />}
        title="Public REST API"
        description="Issue API keys so your apps and integrations can read and write leads on behalf of your tenant. Includes signed webhooks for realtime events."
        planName={planName}
        requiredTier="Starter"
      />
    );
  }


  const handleCreate = async () => {
    if (!name.trim()) { toast('error', 'Name is required'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/api-keys', { name: name.trim() });
      setNewKey({ rawKey: data.rawKey, name: data.name });
      setShowCreate(false);
      setName('');
      load();
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to create key');
    } finally { setCreating(false); }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    try {
      await api.delete(`/api-keys/${revokeId}`);
      toast('success', 'Key revoked');
      setRevokeId(null);
      load();
    } catch { toast('error', 'Failed to revoke'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#6366F1]" />
              API Keys
            </CardTitle>
            <CardDescription>Authenticate calls to <code className="text-xs">POST /api/v1/leads</code></CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New API Key
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" />
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No API keys yet. Create one to start integrating.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {keys.map(k => (
                <div key={k.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{k.name}</p>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">
                      {k.keyPrefix}…{' '}
                      <span className="font-sans text-[10px] text-slate-400">(prefix only)</span>
                      {k.revokedAt && <span className="text-red-500 ml-1">· revoked</span>}
                    </p>
                    <p className="text-[10px] text-amber-600 mt-0.5">
                      Full key is shown only once, at creation. Lost it? Create a new key.
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Created {formatDateTime(k.createdAt)}
                      {k.lastUsedAt && ` · last used ${formatDateTime(k.lastUsedAt)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" title="Copy prefix (identifier only — not the full key)"
                      onClick={() => {
                        navigator.clipboard.writeText(k.keyPrefix);
                        toast('success', 'Prefix copied (identifier only). The full key was shown once at creation — create a new key if you lost it.');
                      }}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    {!k.revokedAt && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        title="Revoke key"
                        onClick={() => setRevokeId(k.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
          <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-1">New API Key</h2>
            <p className="text-xs text-slate-500 mb-4">Give it a label so you can identify it later.</p>
            <Input
              autoFocus
              placeholder="e.g. Website form"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New-key reveal modal — shown ONCE */}
      {newKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-900">Save this key now</h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              <strong>{newKey.name}</strong> — this is the ONLY time we'll show the full key.
              Copy and store it somewhere safe.
            </p>
            <div className="bg-slate-900 rounded-lg p-3 flex items-center gap-2">
              <code className="text-xs text-emerald-300 break-all flex-1">{newKey.rawKey}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(newKey.rawKey);
                  toast('success', 'Copied to clipboard');
                }}
                className="p-1.5 hover:bg-slate-700 rounded text-slate-300"
                title="Copy"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setNewKey(null)}>Got it, I saved the key</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!revokeId}
        title="Revoke this API key?"
        message="Calls using this key will stop working immediately. This cannot be undone."
        confirmText="Revoke"
        onConfirm={handleRevoke}
        onCancel={() => setRevokeId(null)}
      />
    </>
  );
}

// ─── Webhooks ─────────────────────────────────────────────────────────────
function WebhooksSection({ unlocked, planName, planLoading }: { unlocked: boolean; planName: string; planLoading: boolean }) {
  const [hooks, setHooks] = useState<WebhookSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSecret, setShowSecret] = useState<{ url: string; secret: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeliveries, setShowDeliveries] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<WebhookSub[]>('/webhooks-out');
      setHooks(data);
    } finally { setLoading(false); }
  };

  // Same gating story as ApiKeysSection — don't fire the GET when locked.
  useEffect(() => {
    if (!unlocked) { setLoading(false); return; }
    load();
  }, [unlocked]);

  if (planLoading) return <SectionLoadingCard />;

  if (!unlocked) {
    return (
      <LockedFeatureCard
        icon={<Webhook className="h-5 w-5 text-[#6366F1]" />}
        title="Outbound Webhooks"
        description="Subscribe your endpoint to lead events (created, updated, merged, deleted) with HMAC-signed payloads, automatic retries, and a delivery log."
        planName={planName}
        requiredTier="Starter"
      />
    );
  }

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/webhooks-out/${deleteId}`);
      toast('success', 'Webhook deleted');
      setDeleteId(null);
      load();
    } catch { toast('error', 'Failed to delete'); }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await api.patch(`/webhooks-out/${id}`, { isActive });
      load();
    } catch { toast('error', 'Failed to toggle'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="h-4 w-4 text-[#6366F1]" />
              Webhooks
            </CardTitle>
            <CardDescription>
              Send lead events (created, updated, merged, deleted) to your endpoint
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New Webhook
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" />
            </div>
          ) : hooks.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No webhooks configured. Add one to get realtime lead events.
            </p>
          ) : (
            <div className="space-y-3">
              {hooks.map(h => (
                <div key={h.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-slate-800 truncate" title={h.url}>{h.url}</p>
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        {h.events.map(e => (
                          <span key={e} className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">
                            {e}
                          </span>
                        ))}
                      </div>
                      {h.lastDeliveryAt && (
                        <p className={`text-[10px] mt-1 ${h.lastDeliveryStatus === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
                          Last: {h.lastDeliveryStatus} · {formatDateTime(h.lastDeliveryAt)}
                          {h.consecutiveFailures > 0 && ` · ${h.consecutiveFailures} consecutive failure(s)`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggle(h.id, !h.isActive)}
                        className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                          h.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {h.isActive ? 'Active' : 'Paused'}
                      </button>
                      <Button size="sm" variant="outline" onClick={() => setShowDeliveries(h.id)} title="View deliveries">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setDeleteId(h.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <CreateWebhookModal
          onClose={() => setShowCreate(false)}
          onCreated={(secret, url) => { setShowSecret({ url, secret }); setShowCreate(false); load(); }}
        />
      )}

      {showSecret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-900">Save the signing secret</h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Use this secret to verify webhook authenticity (HMAC-SHA256 of the request body, sent
              in the <code className="text-xs">X-BrixLead-Signature</code> header). Shown ONCE.
            </p>
            <div className="bg-slate-900 rounded-lg p-3 flex items-center gap-2 mb-2">
              <code className="text-xs text-emerald-300 break-all flex-1">{showSecret.secret}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(showSecret.secret); toast('success', 'Copied'); }}
                className="p-1.5 hover:bg-slate-700 rounded text-slate-300"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400">Will deliver to: {showSecret.url}</p>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setShowSecret(null)}>Got it</Button>
            </div>
          </div>
        </div>
      )}

      {showDeliveries && (
        <DeliveriesModal id={showDeliveries} onClose={() => setShowDeliveries(null)} />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Delete this webhook?"
        message="No more events will be sent to this URL. Past delivery logs will also be removed."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
}

function CreateWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string, url: string) => void }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['lead.created']);
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const toggleEvent = (e: string) => {
    setEvents(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  };

  const handleCreate = async () => {
    if (!/^https?:\/\//.test(url)) { toast('error', 'URL must start with http(s)://'); return; }
    if (events.length === 0) { toast('error', 'Pick at least one event'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/webhooks-out', { url, events, description: description || undefined });
      onCreated(data.secret, data.url);
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to create webhook');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">New Webhook</h2>
        <p className="text-xs text-slate-500 mb-4">We'll POST events to your endpoint with HMAC-SHA256 signature.</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-normal text-slate-600">URL</label>
            <Input placeholder="https://your-domain.com/webhooks/brixlead" value={url} onChange={e => setUrl(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-normal text-slate-600 mb-1 block">Events</label>
            <div className="flex flex-wrap gap-2">
              {ALL_EVENTS.map(e => (
                <label key={e} className={`px-3 py-1.5 rounded-full text-xs cursor-pointer border ${
                  events.includes(e)
                    ? 'bg-[#6366F1] text-white border-[#6366F1]'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#6366F1]'
                }`}>
                  <input type="checkbox" className="hidden" checked={events.includes(e)} onChange={() => toggleEvent(e)} />
                  {e}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-normal text-slate-600">Description (optional)</label>
            <Input placeholder="Internal note about this webhook" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeliveriesModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/webhooks-out/${id}/deliveries`)
      .then(r => setDeliveries(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl mx-4 max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-slate-900">Recent Deliveries</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-[#6366F1] mx-auto my-8" />
          ) : deliveries.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No deliveries yet</p>
          ) : (
            <div className="space-y-2">
              {deliveries.map((d: any) => {
                const ok = !!d.succeededAt;
                return (
                  <div key={d.id} className={`border rounded-lg p-2.5 text-xs ${ok ? 'border-emerald-100 bg-emerald-50/30' : 'border-red-100 bg-red-50/30'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{d.event}</span>
                      <span className={ok ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {d.responseStatus ?? 'error'} · attempt {d.attemptCount}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{formatDateTime(d.createdAt)}</p>
                    {d.errorMessage && <p className="text-[10px] text-red-500 mt-1 font-mono">{d.errorMessage}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
